import { useDebounce, useRequest } from "ahooks";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { getChats } from "helpers/backend_helper";
import { NotificationContext } from "./notificationContext";
import { AdminContext } from "./adminContext";
import { SocketContext } from "./socketContext";
import { STATUSES } from "pages/Main/Components/AdminPanel/Chats";
import { UploadFile } from "antd";
import { Pagination } from "pages/Main/Components/AdminPanel/Options";
import { useChatLoadReady } from "hooks/useChatLoadReady";
import { useSearchParams } from "react-router-dom";
import { PROP_NAME_CHOOSEN_USER_CHAT_ID } from "helpers/constants";

export type Chat = {
  client: {
    avatar: string | null;
    cityId: number;
    description: string | null;
    email: string;
    firstName: string;
    id: string;
    lastName: string;
    patronymic: string | null;
  };
  mainUserId: string;
  clientId: string;
  createdAt: string;
  id: string;
  lastMessage: any[];
  status: string;
  support: string;
  unreadMessages: number;
  updatedAt: string;
  partnerOnline: boolean;
};

export type FileDataType = {
  filePath: string;
  originalName: string;
};

export type BunchFilesType = {
  files: FileDataType[];
  status: "DONE" | "LOADING" | "ERROR" | "READY";
};

export type Chats = {
  count: number;
  chats: Chat[];
};

export type ContextProps = {
  chats: Chats;
  loading: boolean;
  fileList: UploadFile[];
  bunchFiles: BunchFilesType;
  paginationChats: React.MutableRefObject<Pagination>;
  // setPaginationChats: React.Dispatch<React.SetStateAction<Pagination>>;
  resetInputFields: () => void;
  setChats: React.Dispatch<Chats>;
  setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  setBunchFiles: React.Dispatch<React.SetStateAction<BunchFilesType>>;
  debouncedSearch: string;
  refreshUsersByStatus: () => void;
  statusesOfChats: STATUSES[];
  setStatusesOfChat: React.Dispatch<React.SetStateAction<string[]>>;
  setInputSearchFieldByUserData: React.Dispatch<React.SetStateAction<string>>;
  setCurrentChat: React.Dispatch<React.SetStateAction<Chat>>;
  currentChat: Chat;
};

export const ChatsContext = createContext({} as ContextProps);

export const ChatsProvider = ({ children }: { children: JSX.Element }) => {
  const paginationChats = useRef<Pagination>({ skip: 0, take: 10, total: 0 });
  const { localCountry, isAuthorized, accountData } = useContext(AdminContext);
  const { subscribeToUserOnliness, online, setUserChats: setChats, chats } = useContext(SocketContext);
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [bunchFiles, setBunchFiles] = useState<BunchFilesType>({ files: [], status: "READY" });
  const [inputSearchFieldByUserData, setInputSearchFieldByUserData] = useState("");
  const debouncedSearch = useDebounce(inputSearchFieldByUserData, { wait: 500, trailing: true });
  const [searchParams] = useSearchParams();
  const [statusesOfChats, setStatusesOfChat] = useState<STATUSES[]>([]);
  const isReady = useChatLoadReady();
  const [currentChat, setCurrentChat] = useState<Chat>(null);

  const resetInputFields = () => {
    setBunchFiles({ files: [], status: "READY" });
    setFileList([]);
  };

  useEffect(() => {
    if (online.clientId) {
      setChats((prev) => {
        if (!prev?.chats?.length) return prev;
        const chat = prev?.chats.find((ch) => ch.clientId === online.clientId);
        if (chat) chat.partnerOnline = online.online;
        const sliced = prev.chats.slice();
        return { ...prev, chats: sliced };
      });
    }
  }, [online]);

  const { loading, runAsync: refreshUsersByStatus } = useRequest(
    () =>
      getChats(
        localCountry?.iso,
        statusesOfChats,
        {
          pagination: paginationChats.current,
          userData: debouncedSearch,
        },
        { includeUserId: searchParams.get(PROP_NAME_CHOOSEN_USER_CHAT_ID) },
      ),
    {
      ready:
        !!localCountry &&
        isAuthorized &&
        !!accountData?.userRoles.find((v) => v.role === "SUPERSUPPORT" || v.role === "SUPPORT") &&
        !!isReady &&
        !!statusesOfChats[0],
      refreshDeps: [JSON.stringify(localCountry), debouncedSearch, statusesOfChats],
      onError: (error: any) => {
        console.error(error);
        openNotificationWithIcon("error", error.response?.data?.message || error.message);
      },
      onSuccess: (data) => {
        if (data) {
          paginationChats.current = { ...paginationChats.current, total: data.count };
          data.chats?.forEach((chat) => {
            subscribeToUserOnliness(chat.clientId);
          });
          if (paginationChats.current.skip) {
            setChats((prev) => ({ chats: [...prev.chats, ...data.chats], count: data.count }));
          } else {
            setChats(data);
          }
        }
      },
    },
  );

  return (
    <ChatsContext.Provider
      value={{
        chats,
        loading,
        fileList,
        bunchFiles,
        paginationChats,
        statusesOfChats,
        debouncedSearch,
        currentChat,
        setChats,
        setFileList,
        setBunchFiles,
        setCurrentChat,
        resetInputFields,
        refreshUsersByStatus,
        setStatusesOfChat,
        setInputSearchFieldByUserData,
      }}
    >
      {children}
    </ChatsContext.Provider>
  );
};
